/**
 * Cálculo compartido de campos derivados de incidentes (edad, antigüedad, rangos)
 * basados en fechas. Usado en form, Excel export y PDF.
 */
import moment from 'moment-timezone';

export function parseYearsNumber(value: any): number | null {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value >= 0 ? Math.trunc(value) : 0;
  const match = String(value).match(/-?\d+/);
  if (!match) return null;
  const parsed = parseInt(match[0], 10);
  if (Number.isNaN(parsed)) return null;
  return parsed >= 0 ? parsed : 0;
}

export function computeYearsBetween(startDate: any, incidentDate: any): number | null {
  if (!startDate || !incidentDate) return null;
  const start = moment(startDate);
  const incident = moment(incidentDate);
  if (!start.isValid() || !incident.isValid()) return null;
  const years = incident.diff(start, 'years');
  return years >= 0 ? years : 0;
}

export type DurationParts = {
  years: number;
  months: number;
  days: number;
};

export function computeDurationPartsBetween(startDate: any, incidentDate: any): DurationParts | null {
  if (!startDate || !incidentDate) return null;
  const start = moment(startDate);
  const incident = moment(incidentDate);
  if (!start.isValid() || !incident.isValid()) return null;
  if (incident.isBefore(start)) {
    return {years: 0, months: 0, days: 0};
  }

  const years = incident.diff(start, 'years');
  const afterYears = start.clone().add(years, 'years');
  const months = incident.diff(afterYears, 'months');
  const afterMonths = afterYears.clone().add(months, 'months');
  const days = incident.diff(afterMonths, 'days') + 1;

  return {years, months, days};
}

export function formatYearsLabel(years: number | null): string | null {
  if (years === null) return null;
  return `${years} año${years === 1 ? '' : 's'}`;
}

export function toAgeRange(ageYears: number | null | undefined): string | null {
  if (ageYears === null || ageYears === undefined) return null;
  if (ageYears < 20) return '< 20';
  if (ageYears <= 29) return '20 - 29';
  if (ageYears <= 39) return '30 - 39';
  if (ageYears <= 49) return '40 - 49';
  if (ageYears <= 59) return '50 - 59';
  return '60+';
}

export function toExperienceRange(years: number | null | undefined): string | null {
  if (years === null || years === undefined) return null;
  if (years <= 1) return '1 o menos';
  if (years <= 3) return '1 a 3 años';
  if (years <= 6) return '3 a 6 años';
  return 'mayor a 6';
}

export type IncidentRecordLike = {
  birthDate?: any;
  incidentDate?: any;
  dateFrom?: any;
  ageYears?: any;
  age?: any;
  ageRange?: any;
  companyStartDate?: any;
  contractStartDate?: any;
  companyExperienceYears?: any;
  companyExperienceMonths?: any;
  companyExperienceDays?: any;
  companyExperienceRange?: any;
  jobStartDate?: any;
  workExperienceYears?: any;
  workExperienceMonths?: any;
  workExperienceDays?: any;
  workExperience?: any;
  workExperienceRange?: any;
  [key: string]: any;
};

/**
 * Calcula los campos derivados (edad, antigüedad, rangos) a partir de las fechas del record.
 * Si el record ya tiene valores calculados, los respeta cuando no hay fechas para calcular.
 * Devuelve un objeto con los campos derivados aplicados (no muta el input).
 */
export function computeIncidentDerivedFromDates(record: IncidentRecordLike): Record<string, any> {
  const result = {...record};
  const incidentDate = record?.incidentDate || record?.dateFrom;

  // Edad: birthDate + incidentDate → ageYears, age, ageRange
  const ageYears =
    computeYearsBetween(record?.birthDate, incidentDate) ??
    parseYearsNumber(record?.ageYears ?? record?.age);
  if (ageYears !== null) {
    result.ageYears = ageYears;
    result.age = ageYears;
    result.ageRange = toAgeRange(ageYears);
  }

  // Antigüedad empresa: companyStartDate/contractStartDate + incidentDate
  const companyStartDate = record?.companyStartDate || record?.contractStartDate;
  if (companyStartDate) {
    result.companyStartDate = companyStartDate;
    result.contractStartDate = companyStartDate;
  }
  const companyDuration = computeDurationPartsBetween(companyStartDate, incidentDate);
  const companyYears = companyDuration?.years ?? computeYearsBetween(companyStartDate, incidentDate);
  if (companyYears !== null) {
    result.companyExperienceYears = companyYears;
    if (companyDuration) {
      result.companyExperienceMonths = companyDuration.months;
      result.companyExperienceDays = companyDuration.days;
    }
    result.companyExperienceRange = toExperienceRange(companyYears);
  }

  // Antigüedad cargo: jobStartDate/contractStartDate + incidentDate (o valor existente)
  const jobStartDate = record?.jobStartDate || record?.contractStartDate;
  const computedJobDuration = computeDurationPartsBetween(jobStartDate, incidentDate);
  const computedJobYears = computedJobDuration?.years ?? computeYearsBetween(jobStartDate, incidentDate);
  const editableJobYears = parseYearsNumber(record?.workExperienceYears ?? record?.workExperience);
  const jobYears = computedJobYears ?? editableJobYears;
  if (jobStartDate) {
    result.jobStartDate = jobStartDate;
  }
  if (jobYears !== null) {
    result.workExperienceYears = jobYears;
    if (computedJobDuration) {
      result.workExperienceMonths = computedJobDuration.months;
      result.workExperienceDays = computedJobDuration.days;
    }
    result.workExperienceRange = toExperienceRange(jobYears);
    result.workExperience = formatYearsLabel(jobYears);
  }

  return result;
}
